import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import type { ModuleId, PermissionLevel } from '../../types/rbac';
import { hasMinLevel } from '../../types/rbac';
import { Text, Center } from '@mantine/core';

interface ProtectedRouteProps {
  children: React.ReactNode;
  module?: ModuleId;
  minLevel?: PermissionLevel;
}

export default function ProtectedRoute({ children, module, minLevel = 'viewer' }: ProtectedRouteProps) {
  const { isAuthenticated, sessionChecked } = useAuthStore();
  const { isLoaded, getModuleAccess, myRole } = useRoleStore();

  // Wait for session restore before redirecting — prevents flash redirect on direct URL navigation
  if (!sessionChecked) return null;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // If no module specified, just check auth
  if (!module) return <>{children}</>;

  // OpenGov super admin / EOC director bypass
  if (myRole?.roleSlug === 'super_admin' || myRole?.roleSlug === 'eoc_director') {
    return <>{children}</>;
  }

  // If roles haven't loaded yet, allow (don't flash redirect)
  if (!isLoaded) return <>{children}</>;

  const access = getModuleAccess(module);
  if (!hasMinLevel(access.level, minLevel)) {
    return (
      <Center p="xl" style={{ minHeight: 300 }}>
        <Text c="dimmed">You don't have access to this module.</Text>
      </Center>
    );
  }

  return <>{children}</>;
}
